import type { BudgetHealth, BudgetHealthState } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useFormatters } from "@/lib/format";

/**
 * ADP-89: budget-health state → semantic color (see CLAUDE.md design tokens).
 * `budget_limited` is a problem (throttled / missed impressions) → destructive;
 * `near_exhaustion` → warning; `normal` → success; `unknown` → neutral.
 */
const STATE_VARIANT: Record<BudgetHealthState, "success" | "warning" | "destructive" | "neutral"> =
  {
    normal: "success",
    near_exhaustion: "warning",
    budget_limited: "destructive",
    unknown: "neutral",
  };

interface BudgetHealthBadgeProps {
  health: BudgetHealth;
  className?: string;
}

/**
 * Presentational badge for a campaign/portfolio `BudgetHealth` reading. The value
 * is always a server-side ESTIMATE (`derived: true`), so the tooltip states that
 * explicitly and surfaces the underlying spend/budget (or the `reason` when the
 * state can't be derived). Colors come from semantic Badge variants, so it is
 * dark-mode-safe without extra work.
 */
export function BudgetHealthBadge({ health, className }: BudgetHealthBadgeProps) {
  const { t } = useTranslation("budgetHealth");
  const { formatCurrency, formatPercent } = useFormatters();

  const label = t(`state.${health.state}`);
  const variant = STATE_VARIANT[health.state];

  const lines: string[] = [];
  if (health.state === "unknown") {
    lines.push(health.reason ? t(`tooltip.reason.${health.reason}`) : t("tooltip.unknownGeneric"));
  } else {
    lines.push(t(`tooltip.${health.state}`));
    if (health.spend != null && health.budget != null) {
      const currency = health.currency ?? "USD";
      lines.push(
        t("tooltip.usage", {
          spend: formatCurrency(health.spend, currency),
          budget: formatCurrency(health.budget, currency),
          utilization: health.utilization != null ? formatPercent(health.utilization) : "—",
        }),
      );
    }
  }
  lines.push(t("tooltip.estimate"));

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <Badge
              variant={variant}
              className={className}
              aria-label={t("ariaLabel", { state: label })}
            />
          }
        >
          {label}
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex max-w-[220px] flex-col gap-1 text-left">
            {lines.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
